import { ApiError, env, fetchWithAuth } from '@botto/core';

type QueryValue = string | number | boolean | undefined | null | Array<string | number | boolean>;

function joinUrl(path: string): string {
  const base = env.trackerApiUrl.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function applyQuery(url: URL, query?: Record<string, QueryValue>): void {
  if (!query) return;
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null && item !== '') {
          url.searchParams.append(k, String(item));
        }
      }
    } else {
      url.searchParams.set(k, String(v));
    }
  }
}

function buildHref(path: string, query?: Record<string, QueryValue>): string {
  const joined = joinUrl(path);
  const url = new URL(joined, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  applyQuery(url, query);
  return joined.startsWith('http') ? url.href : `${url.pathname}${url.search}`;
}

function formatDetail(detail: unknown): string {
  if (detail == null) return 'Request failed';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : JSON.stringify(d))).join('; ');
  }
  return JSON.stringify(detail);
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (res.status === 204) return null as T;

  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const detail =
      data && typeof data === 'object' && 'detail' in data
        ? (data as { detail: unknown }).detail
        : data;
    throw new ApiError(formatDetail(detail), res.status, data);
  }

  if (!isJson) {
    if (!data) return null as T;
    try {
      return JSON.parse(data as string) as T;
    } catch {
      return data as T;
    }
  }
  return data as T;
}

async function trackerFetch(path: string, init: RequestInit, query?: Record<string, QueryValue>): Promise<Response> {
  return fetchWithAuth(buildHref(path, query), init, 'tracker');
}

/** GET JSON from tracker API (audience `tracker`). */
export async function trackerGet<T>(path: string, query?: Record<string, QueryValue>): Promise<T> {
  const res = await trackerFetch(path, { method: 'GET' }, query);
  return parseResponse<T>(res);
}

/** Download a binary/text attachment from tracker API and trigger a browser save. */
export async function trackerDownload(
  path: string,
  query: Record<string, QueryValue> | undefined,
  filename: string,
): Promise<void> {
  const res = await trackerFetch(path, { method: 'GET' }, query);
  if (!res.ok) {
    await parseResponse(res);
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** POST JSON to tracker API. */
export async function trackerPost<T>(
  path: string,
  body: unknown,
  query?: Record<string, QueryValue>,
): Promise<T> {
  const res = await trackerFetch(
    path,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    query,
  );
  return parseResponse<T>(res);
}

/** PATCH JSON to tracker API. */
export async function trackerPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await trackerFetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

/** PUT JSON to tracker API. */
export async function trackerPut<T>(path: string, body: unknown): Promise<T> {
  const res = await trackerFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

/** DELETE on tracker API. */
export async function trackerDelete<T = null>(path: string): Promise<T> {
  const res = await trackerFetch(path, { method: 'DELETE' });
  return parseResponse<T>(res);
}

/** POST multipart/form-data (do not set Content-Type — browser sets boundary). */
export async function trackerPostForm<T>(path: string, form: FormData): Promise<T> {
  const res = await trackerFetch(path, {
    method: 'POST',
    body: form,
  });
  return parseResponse<T>(res);
}
